import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, ShieldCheck, Zap, Users } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back to Blueconnect");
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  const fillDemo = () => {
    setEmail("admin@blueconnect.io");
    setPassword("Admin@123456");
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex">
        <div className="absolute inset-0 bc-grid-bg opacity-20" />
        <div className="relative">
          <Link to="/"><Logo textClassName="text-white" mono /></Link>
        </div>
        <div className="relative">
          <h2 className="font-heading text-3xl font-extrabold leading-tight">Your entire customer journey. Connected.</h2>
          <p className="mt-3 max-w-md text-primary-foreground/80">Capture leads, close deals, collect payments and serve customers — all from one intelligent platform.</p>
          <div className="mt-8 space-y-3">
            {[[ShieldCheck, "Secure multi-tenant architecture"], [Zap, "Real-time War Room & SLA"], [Users, "Team Live & workforce visibility"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-3 text-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15"><Icon className="h-4 w-4" /></span>{t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-primary-foreground/60">Connect. Sell. Serve. Grow.</div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Link to="/"><Logo /></Link></div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Log in to your workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back. Enter your details to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <span className="text-xs text-muted-foreground">Forgot?</span>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {error && <p className="rounded-lg bg-critical/10 px-3 py-2 text-sm text-critical" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Log in <ArrowRight className="ml-1.5 h-4 w-4" /></>}
            </Button>
          </form>

          <button onClick={fillDemo} className="mt-3 w-full rounded-lg border border-dashed border-border py-2 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary" data-testid="fill-demo-btn">
            Use demo account (admin@blueconnect.io)
          </button>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            New to Blueconnect? <Link to="/signup" className="font-medium text-primary hover:underline" data-testid="go-signup-link">Start your free trial</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
